/**
 * RevenueByProjectTile — Dashboard widget (Feb 2026).
 *
 * Shows the top N projects by actual INVOICED revenue in the selected
 * window (30d default, 90d / 1y / All toggles). Different from
 * ProjectProfitabilityTile: this one tracks money that hit QBO, not
 * computed labour × rate.
 *
 * Data: GET /api/analytics/projects/revenue?days={days}&limit=5
 *
 * Permissions: admins + supervisors only.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { DollarSign, Loader2, Receipt, ArrowRight, Briefcase } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { key: 30, label: '30d' },
  { key: 90, label: '90d' },
  { key: 365, label: '1y' },
  { key: 0, label: 'All' },
];

// $1,234 / $12.4k / $1.2M — keeps the tile breathable when the numbers grow.
const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 10_000) return `$${(v / 1_000).toFixed(1)}k`;
  if (Math.abs(v) >= 1_000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `$${v.toFixed(0)}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return '—'; }
};

export default function RevenueByProjectTile() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/analytics/projects/revenue?days=${days}&limit=5`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => {
        if (cancelled) return;
        const status = e.response?.status;
        if (status === 403) setError('Admin-only view');
        else setError(e.response?.data?.detail || e.message || 'Could not load revenue');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days, token]);

  const rows = data?.rows || [];
  const totals = data?.totals || { revenue: 0, invoices: 0, projects: 0 };

  return (
    <div
      className="bg-[#1A2433] rounded-lg shadow-md border border-[#2D3748] flex flex-col"
      data-testid="revenue-by-project-tile"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2D3748]">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-emerald-300" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">Revenue by Project</h3>
            <p className="text-[10px] text-slate-400">Invoiced amounts (HinesTime + QuickBooks + Daily Site)</p>
          </div>
        </div>
        <div className="flex gap-1" data-testid="revenue-period-switch">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setDays(p.key)}
              className={`text-[10px] px-2 py-1 rounded-md transition-colors ${
                days === p.key
                  ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40'
                  : 'text-slate-400 hover:text-white border border-transparent'
              }`}
              data-testid={`revenue-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-3 flex items-baseline justify-between gap-2 border-b border-[#2D3748]">
        <div>
          <div className="text-2xl font-bold text-white" data-testid="revenue-total">
            {fmtMoney(totals.revenue)}
          </div>
          <div className="text-[11px] text-slate-400" data-testid="revenue-subtotals">
            {totals.invoices} invoice{totals.invoices === 1 ? '' : 's'}{totals.qb_invoices ? ` (${totals.qb_invoices} in QBO)` : ''} · {totals.projects ? `${totals.projects} project${totals.projects === 1 ? '' : 's'}` : 'by customer'}{totals.paid ? ` · ${fmtMoney(totals.paid)} paid` : ''}
          </div>
        </div>
        <div className="text-[10px] text-slate-500 text-right">
          {days === 0 ? 'All time' : `Last ${days} days`}
        </div>
      </div>

      <div className="flex-1 min-h-[10rem] p-2" data-testid="revenue-rows">
        {loading ? (
          <div className="flex items-center justify-center h-full text-slate-400 text-xs">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-slate-500 text-xs italic">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500 text-xs italic text-center px-4">
            <Receipt className="w-6 h-6 mb-2 text-slate-600" />
            No invoices in this window yet.
            <span className="text-[10px] mt-1 text-slate-600">
              Issue a job invoice (Bill Out) or a Daily Site Invoice to populate this widget.
            </span>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {rows.map((r, idx) => {
              // Bar fill is proportional to the top row's revenue, so the
              // chart stays readable even when one project dwarfs the rest.
              const maxRevenue = rows[0]?.revenue || 1;
              const widthPct = Math.max(4, Math.round((r.revenue / maxRevenue) * 100));
              return (
                <li
                  key={r.project_id || `unlinked-${idx}`}
                  className="px-2 py-1.5 rounded hover:bg-[#0F1923] transition-colors"
                  data-testid={`revenue-row-${idx}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-xs">
                        {r.project_number && (
                          <span className="font-mono text-emerald-400 text-[10px]">{r.project_number}</span>
                        )}
                        <span className="text-white truncate">{r.project_name}</span>
                        {r.qb_project_id && (
                          <span
                            className="text-[9px] px-1 rounded bg-emerald-500/10 text-emerald-300 flex-shrink-0"
                            title={`Linked to QBO Project #${r.qb_project_id}`}
                          >
                            QBO
                          </span>
                        )}
                      </div>
                      {r.customer_name && (
                        <div className="text-[10px] text-slate-500 truncate">{r.customer_name}</div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-emerald-300">{fmtMoney(r.revenue)}</div>
                      <div className="text-[9px] text-slate-500">
                        {r.invoices} inv · {fmtDate(r.last_invoiced_at)}
                      </div>
                    </div>
                  </div>
                  <div className="h-1 rounded-full bg-[#0F1923] overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600"
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <button
        type="button"
        onClick={() => navigate('/jobs?status=invoiced')}
        className="text-[11px] text-emerald-300 hover:text-emerald-200 flex items-center justify-center gap-1 py-2 border-t border-[#2D3748]"
        data-testid="revenue-view-all"
      >
        <Briefcase className="w-3 h-3" />
        View invoiced jobs
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
